import React from "react";
import { Drawer, Card, Descriptions, Divider, Tag } from "antd";
import { UserOutlined, PhoneOutlined, CarOutlined } from "@ant-design/icons";

export const CustomerView: React.FC<any> = ({ open, onClose, customer }) => {
  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          <UserOutlined className="text-blue-600" />
          <span>Customer & Inquiry Details</span>
        </div>
      }
      open={open}
      onClose={onClose}
      width={700}
      destroyOnClose
      className="bg-gray-50"
    >
      {customer ? (
        <div className="space-y-6">
          {/* Customer Profile Card */}
          <Card
            className="shadow-sm border-gray-200"
            title="Customer Profile"
            size="small"
          >
            <Descriptions column={2} bordered size="small" layout="vertical">
              <Descriptions.Item
                label={<span className="text-gray-500">Full Name</span>}
              >
                <span className="font-semibold text-gray-800">
                  {customer.name}
                </span>
              </Descriptions.Item>
              <Descriptions.Item
                label={<span className="text-gray-500">Contact Number</span>}
              >
                <span className="flex items-center gap-2">
                  <PhoneOutlined className="text-green-600" />
                  {customer.mobile}
                </span>
              </Descriptions.Item>

              {customer.remarks && (
                <Descriptions.Item label="Remarks" span={2}>
                  <p className="whitespace-pre-wrap text-sm text-gray-600">
                    {customer.remarks}
                  </p>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Divider orientation="left" className="text-gray-600 font-medium">
            Inquiries History
          </Divider>

          {/* Inquiries List */}
          {customer.inquiries && customer.inquiries.length > 0 ? (
            <div className="space-y-4">
              {customer.inquiries.map((inq, idx) => (
                <Card
                  key={idx}
                  className="shadow-sm border-l-4 border-l-blue-500"
                  size="small"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-lg font-bold text-gray-800 m-0">
                          {inq.inquiryNumber}
                        </h4>
                        <Tag
                          color={
                            inq.status?.toLowerCase() === "paid"
                              ? "green"
                              : "orange"
                          }
                        >
                          {inq.status}
                        </Tag>
                        {inq.isPaidCompany != 0 && (
                          <Tag color="cyan">Company Paid</Tag>
                        )}

                        {inq.isBatapola != 0 && (
                          <Tag color="geekblue">Batapola</Tag>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-gray-600">
                        <CarOutlined />
                        <span>
                          {inq.vehicleType} - {inq.vehicleModel}
                        </span>
                        {inq.vehicle_color && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-500">
                            {inq.vehicle_color}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-800">
                        <span className="text-xs text-gray-500 font-normal mr-1">
                          LKR
                        </span>
                        {parseFloat(inq.total as any).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">Total Amount</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                    <div>
                      <span className="block text-xs text-gray-500">
                        Paid Amount
                      </span>
                      <span className="font-semibold text-green-600">
                        LKR {parseFloat(inq.paid as any).toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500">
                        Due Amount
                      </span>
                      <span
                        className={`font-semibold ${
                          parseFloat(inq.due as any) > 0
                            ? "text-red-500"
                            : "text-gray-500"
                        }`}
                      >
                        LKR {parseFloat(inq.due as any).toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500">
                        Engine No.
                      </span>
                      <span className="font-medium text-gray-800">
                        {inq.engine_number || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500">
                        Chassis No.
                      </span>
                      <span className="font-medium text-gray-800">
                        {inq.chassis_number || "N/A"}
                      </span>
                    </div>
                  </div>

                  {inq.priceList && Object.keys(inq.priceList).length > 0 && (
                    <div className="mt-4 border-t pt-3">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Price Breakdown
                      </p>
                      <div className="flex justify-between items-center my-3">
                        <p className="my-0 py-0">Vehicle Price: LKR {parseFloat(inq.total as any).toLocaleString()}.00</p>
                        <p className="my-0 py-0">Downpayment: LKR {parseFloat(inq.downpayment as any).toLocaleString()}.00</p>
                        <p className="my-0 py-0">Loan Amount: LKR {parseFloat(inq.loanamount as any).toLocaleString()}.00</p>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        {Object.entries(inq.priceList).map(([key, value]) => (
                          <div
                            key={key}
                            className="flex justify-between border-b border-gray-100 py-1 last:border-0"
                          >
                            <span className="text-gray-600 capitalize">
                              {key.replace(/_/g, " ")}
                            </span>
                            <span className="font-medium text-gray-800">
                              {parseFloat(value as any).toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                },
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 bg-white rounded border border-dashed">
              <CarOutlined className="text-2xl mb-2" />
              <p>No inquiries found for this customer.</p>
            </div>
          )}
        </div>
      ) : null}
    </Drawer>
  );
};
